<script lang="js">
import axios from "axios";
import Swal from "sweetalert2";
import HelperFunctions from "../utils/HelperFunctions";

export default {
  data() {
    return {
      hp: new HelperFunctions(),
      surname: "",
      firstname: "",
      othername: "",
      email: "",
      phone: "",
      dob: "",
      maxDob: this.calculateMaxDob(),
      entry_year: "2025/2026",
      programType: '',
      programMode: '',
      course: '',
      programOptions: {
        'National Diploma': {
          modes: {
            'Part Time': [
              'Business Administration and Management',
              'Electrical Engineering',
              'Computer Engineering',
              'Computer Science',
              'Statistics',
            ],
            'Full Time': [
              'Business Administration and Management',
              'Electrical Engineering',
              'Computer Engineering',
              'Computer Science',
              'Statistics',
            ],
          },
        },
        'Higher National Diploma': {
          modes: {
            'Part Time': [
              'Business Administration and Management',
              'Electrical Engineering',
              'Computer Engineering',
              'Computer Science',
              'Statistics',
            ],
            'Full Time': [
              'Business Administration and Management',
              'Electrical Engineering',
              'Computer Engineering',
              'Computer Science',
              'Statistics',
            ],
          },
        },
      },
      loading: false,
      errorMessage: "",
    };
  },
  methods: {
    calculateMaxDob() {
      const today = new Date();
      today.setFullYear(today.getFullYear() - 16); // Subtract 16 years
      return today.toISOString().split("T")[0]; // Format as YYYY-MM-DD
    },
    resetForm() {
      this.surname = "";
      this.firstname = "";
      this.othername = "";
      this.email = "";
      this.programType = "";
      this.programMode = "";
      this.course = "";
      this.phone = "";
      this.dob = "";
      this.entryYear = "2025/2026";
    },
    async submitForm() {
      // Validate email before submission
      if (!this.hp.isValidEmail(this.email)) {
        console.log("Please enter a valid email address.");
        this.errorMessage = "Please enter a valid email address.";
        return;
      }

      if (!this.formattedProgram) {
        this.errorMessage = "Please select a valid program.";
        return;
      }

      const payload = {
        surname: this.surname,
        firstname: this.firstname,
        othername: this.othername,
        email: this.email,
        phone: this.phone.toString(),
        dob: this.dob,
        entry_year: this.entry_year,
        program: this.formattedProgram,
      };

      this.loading = true;
      this.errorMessage = "";

      try {
        const apiUrl = `${process.env.VUE_APP_API_BASE_URL}/biodata`;

        await axios.post(
          apiUrl,
          payload,
          {
            headers: {
              Authorization: "Bearer fcpportals@new",
              "Content-Type": "application/json",
              "Accept": "application/json"
            },
          }
        );

        Swal.fire({
          title: "Successful!",
          text: "Registration successful! Check email for login details.",
          icon: "success"
        });
        // Clear the form after success
        this.resetForm();
      } catch (error) {
        this.errorMessage = error.response?.data?.message || "An error occurred";
      } finally {
        this.loading = false;
      }
    },

  },
  computed: {
    // availableModes() {
    //   return this.programOptions[this.programType]?.modes || {};
    // },
    availableModes() {
      return this.programType && this.programOptions[this.programType]
        ? this.programOptions[this.programType].modes
        : {};
    },
    // availableCourses() {
    //   return this.availableModes[this.programMode] || [];
    // },
    availableCourses() {
      return this.programMode && this.availableModes[this.programMode]
        ? this.availableModes[this.programMode]
        : []; // Return empty array if programMode is null
    },
    formattedProgram() {
      if (this.programType && this.programMode && this.course) {
        let shortProgramType;

        if (this.programType === "Higher National Diploma") {
          shortProgramType = "HND";
        } else if (this.programType === "National Diploma") {
          shortProgramType = "ND";
        } else {
          shortProgramType = "UNKNOWN";
        }
        return `${shortProgramType} ${this.programMode.toUpperCase()} (${this.course})`;
      }
      return "";
    },
  },
};
</script>

<template>
  <div class="container-fluid bg-body-tertiary">
    <div class="d-flex align-items-center py-4 vh-100">
      <main class="m-auto">
        <form @submit.prevent="submitForm">
          <RouterLink to="/app">
            <img
              src="../assets/logo.png"
              class="mb-4"
              alt=""
              width="96.26"
              height="70"
            />
          </RouterLink>
          <h3 class="mb-5 fw-bold text-uppercase">
            FCP ONLINE APPLICATION 2024/25
          </h3>

          <div class="d-flex border-bottom border-dark-subtle mb-4">
            <h6 class="text-start fw-bold me-2">Basic Details Form (BDF)</h6>
            <p class="mb-0 text-muted" style="font-size: small">
              - Fields with <span class="text-danger">*</span> are required.
            </p>
          </div>

          <div
            v-if="errorMessage"
            class="alert alert-danger alert-dismissible fade show d-flex justify-content-between"
            role="alert"
          >
            <div>
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ errorMessage }}
            </div>

            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="alert"
              aria-label="Close"
            ></button>
          </div>

          <div class="row text-start">
            <div class="col-md-4 mb-3">
              <label for="programType" class="form-label">
                Program Type <span class="text-danger">*</span>
              </label>
              <select
                v-model="programType"
                class="form-select"
                id="programType"
                required
              >
                <option value="" disabled selected>--Select--</option>
                <option
                  v-for="(modes, type) in programOptions"
                  :key="type"
                  :value="type"
                >
                  {{ type }}
                </option>
              </select>
            </div>
            <div class="col-md-4 mb-3">
              <label for="programMode" class="form-label">
                Program Mode <span class="text-danger">*</span>
              </label>
              <select
                v-model="programMode"
                class="form-select"
                id="programMode"
                :disabled="!programType"
                required
              >
                <option value="" disabled selected>--Select--</option>
                <option
                  v-for="(courses, mode) in availableModes"
                  :key="mode"
                  :value="mode"
                >
                  {{ mode }}
                </option>
              </select>
            </div>
            <div class="col-md-4 mb-3">
              <label for="course" class="form-label">
                Course <span class="text-danger">*</span>
              </label>
              <select
                v-model="course"
                class="form-select"
                id="course"
                :disabled="!programMode"
                required
              >
                <option value="" disabled selected>--Select--</option>
                <option
                  v-for="course in availableCourses"
                  :key="course"
                  :value="course"
                >
                  {{ course }}
                </option>
              </select>
            </div>
          </div>

          <div class="row text-start">
            <div class="col-md-4 mb-3">
              <label for="surname" class="form-label">
                Surname <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                v-model="surname"
                id="surname"
                placeholder="Doe"
                required
              />
            </div>
            <div class="col-md-4 mb-3">
              <label for="firstName" class="form-label">
                First Name <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                v-model="firstname"
                id="firstName"
                placeholder="John"
                required
              />
            </div>
            <div class="col-md-4 mb-3">
              <label for="otherName" class="form-label">Other Name</label>
              <input
                type="text"
                class="form-control"
                id="otherName"
                v-model="othername"
                placeholder="Shabba"
              />
            </div>
          </div>

          <div class="row text-start">
            <div class="col-md-4 mb-3">
              <label for="dob" class="form-label">
                Date of Birth <span class="text-danger">*</span>
              </label>
              <input
                type="date"
                v-model="dob"
                class="form-control"
                name="dob"
                id="dob"
                :max="maxDob"
                required
              />
            </div>

            <div class="col-md-4 mb-3">
              <label for="mobileNumber" class="form-label">
                Mobile Number <span class="text-danger">*</span>
              </label>
              <input
                type="tel"
                v-model="phone"
                class="form-control"
                id="mobileNumber"
                placeholder="08012345678"
                required
              />
            </div>
            <div class="col-md-4 mb-3">
              <label for="email" class="form-label">
                Email <span class="text-danger">*</span>
              </label>
              <input
                type="email"
                v-model="email"
                class="form-control"
                id="email"
                placeholder="johndoe@mail.com"
                required
              />
            </div>
          </div>

          <div class="d-grid gap-2 d-md-flex justify-content-md-start mt-4">
            <button type="submit" class="btn btn-primary" :disabled="loading">
              {{ loading ? "Applying..." : "Apply" }}
            </button>
            <button class="btn btn-secondary" type="button" @click="resetForm">
              Reset
            </button>
          </div>

          <hr />

          <p class="mt-5 mb-3 text-body-secondary">© 2022–2025</p>
        </form>
      </main>
    </div>
  </div>
</template>

<style scoped></style>
